import { useEffect, useState, useRef, startTransition } from 'react';
import { useAuth } from '../context/AuthContext';
import * as Sentry from '@sentry/react';

const KEY_LAST_STATE = 'server_last_state';
const KEY_SHOWN_TYPE = 'server_banner_shown_type';

// Constants for polling and health check configuration
const HEALTH_URL = '/health';
const POLL_MS = 4000;
const HEALTH_FAIL_THRESHOLD = 2;
const HEALTH_SUCCESS_THRESHOLD = 1;
const CACHE_STALE_MS = 20000;
const CACHE_PROGRESS_TIMEOUT_MS = 5000;

const CloseButton = ({ onClick, aria, className = '' }) => (
  <button aria-label={aria} className={`cache-close-btn ${className}`} onClick={onClick} title="Close">
    <svg viewBox="0 0 16 16" width="16" height="16" aria-hidden="true" focusable="false">
      <path fill="currentColor" d="M3.22 3.22a.75.75 0 0 1 1.06 0L8 6.94l3.72-3.72a.75.75 0 1 1 1.06 1.06L9.06 8l3.72 3.72a.75.75 0 1 1-1.06 1.06L8 9.06l-3.72 3.72a.75.75 0 1 1-1.06-1.06L6.94 8 3.22 4.28a.75.75 0 0 1 0-1.06z"/>
    </svg>
  </button>
);

const GearIcon = ({ spin = true }) => (
  <svg className={spin ? 'cache-icon spin' : 'cache-icon'} viewBox="0 0 24 24" width="16" height="16" aria-hidden="true" focusable="false">
    <path fill="currentColor" d="M19.14 12.94c.04-.31.06-.63.06-.94s-.02-.63-.06-.94l2.03-1.58a.5.5 0 0 0 .12-.64l-1.92-3.32a.5.5 0 0 0-.6-.22l-2.39.96a7.03 7.03 0 0 0-1.62-.94l-.36-2.54A.5.5 0 0 0 13.9 1h-3.8a.5.5 0 0 0-.49.42l-.36 2.54c-.58.23-1.12.54-1.62.94l-2.39-.96a.5.5 0 0 0-.6.22L2.83 7.08a.5.5 0 0 0 .12.64l2.03 1.58c-.04.31-.06.63-.06.94s.02.63.06.94L2.95 12.75a.5.5 0 0 0-.12.64l1.92 3.32c.13.23.39.32.6.22l2.39-.96c.49.4 1.04.72 1.62.94l.36 2.54c.04.24.25.42.49.42h3.8c.24 0 .45-.18.49-.42l.36-2.54c.58-.23 1.12-.54 1.62-.94l2.39.96c.22.09.47 0 .6-.22l1.92-3.32a.5.5 0 0 0-.12-.64l-2.03-1.58zM12 15.5A3.5 3.5 0 1 1 12 8.5a3.5 3.5 0 0 1 0 7z"/>
  </svg>
);

const RefreshIcon = () => (
  <svg className="cache-icon spin-ccw" viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" focusable="false">
    <path d="M16.5 3.75A8.25 8.25 0 0 1 20.25 12m0 0h-3.375M20.25 12l-2.625 2.625"/>
    <path d="M7.5 20.25A8.25 8.25 0 0 1 3.75 12m0 0h3.375M3.75 12l2.625-2.625"/>
  </svg>
);

// Token refresh buffer in milliseconds (5 minutes before expiry)
// This matches the buffer used in AuthContext for consistency
const TOKEN_REFRESH_BUFFER_MS = 5 * 60 * 1000;

// Startup delay to allow session to fully establish after login
// This prevents race conditions where the banner polls before the backend
// has committed the session transaction or middleware has recognized the JWT
const STARTUP_DELAY_MS = 2000;

// Displays a global banner for:
// - Cache warm-up progress when API is available and warming
// - Server down notice (once per down transition)
// - Server back up notice (once per up transition following a down)
const GlobalCacheBanner = () => {
  const { user } = useAuth();
  const [cacheStatus, setCacheStatus] = useState(null);
  const [visibleType, setVisibleType] = useState(null);
  const currentTypeRef = useRef(null);

  const failCountRef = useRef(0);
  const successCountRef = useRef(0);
  const lastCacheTsRef = useRef(0);
  const lastWarmingRef = useRef(false);

  useEffect(() => {
    let stop = false;
    let timer;
    let startupTimer;

    const decideAndRender = (isUp, prevState) => {
        const now = Date.now();
        const hasFreshCache = lastWarmingRef.current && (now - lastCacheTsRef.current) < CACHE_STALE_MS;

        if (hasFreshCache) {
            startTransition(() => setVisibleType('cache'));
            currentTypeRef.current = 'cache';
            return;
        }

        if (isUp) {
            const shown = localStorage.getItem(KEY_SHOWN_TYPE);
            if (prevState === 'down' && shown !== 'up') {
                startTransition(() => setVisibleType('up'));
                currentTypeRef.current = 'up';
            } else {
                startTransition(() => setVisibleType(null));
                currentTypeRef.current = null;
            }
        } else {
            const shown = localStorage.getItem(KEY_SHOWN_TYPE);
            if (shown !== 'down') {
                startTransition(() => setVisibleType('down'));
                currentTypeRef.current = 'down';
            } else {
                startTransition(() => setVisibleType(null));
                currentTypeRef.current = null;
            }
        }
    };

    const check = async () => {
      // 1) Health check (no auth)
      let isUpSample;
      try {
        const controller = new AbortController();
        const healthTimeoutId = setTimeout(() => controller.abort(), 5000); // allow up to 5s
        const res = await fetch(HEALTH_URL, { signal: controller.signal });
        clearTimeout(healthTimeoutId);
        isUpSample = res.ok;
      } catch (catchError) {
          Sentry.captureException(catchError);
        isUpSample = false;
      }

      if (stop) return;

      // 2) Update stability counters
      if (isUpSample) {
        successCountRef.current += 1;
        failCountRef.current = 0;
      } else {
        failCountRef.current += 1;
        successCountRef.current = 0;
      }

      // 3) Determine stabilized state
      const prevState = localStorage.getItem(KEY_LAST_STATE) || 'up';
      let isUpStable = prevState === 'up';
      if (failCountRef.current >= HEALTH_FAIL_THRESHOLD) {
        isUpStable = false;
      } else if (successCountRef.current >= HEALTH_SUCCESS_THRESHOLD) {
        isUpStable = true;
      }

      // Persist last state if changed
      const newStateStr = isUpStable ? 'up' : 'down';
      if (newStateStr !== prevState) {
        localStorage.setItem(KEY_LAST_STATE, newStateStr);
        localStorage.removeItem(KEY_SHOWN_TYPE); // reset once-only notice on transition
      }

      // 4) If stabilized UP and authenticated, attempt cache-progress
      // We use direct fetch (not apiFetch) to avoid automatic logout on 401 errors.
      // This is crucial during login when the session might not be fully established yet.
      // However, we manually check for token expiry and refresh if needed to prevent
      // 401 errors from stale tokens during normal operation.
      if (isUpStable && user) {
        try {
          const userJson = localStorage.getItem('user');
          if (!userJson) {
            // If user context exists but localStorage is missing the user record,
            // decide and render the appropriate banner but DO NOT return early.
            // Allowing execution to continue ensures the polling loop always schedules
            // the next check, preventing it from getting stuck until remount.
            decideAndRender(true, prevState);
          } else {
            const storedUser = JSON.parse(userJson);
            if (storedUser && storedUser.token) {
              // Check if token is near expiry and needs refresh
              let tokenToUse = storedUser.token;
              try {
                // Parse token expiry time (JWT payload is base64-encoded)
                const tokenPayload = JSON.parse(atob(storedUser.token.split('.')[1]));
                const tokenExpiry = tokenPayload.exp * 1000; // Convert to milliseconds
                const shouldRefresh = Date.now() >= (tokenExpiry - TOKEN_REFRESH_BUFFER_MS);
                
                // If token is near expiry, try to refresh it
                if (shouldRefresh && storedUser.refreshToken) {
                  try {
                    const refreshResponse = await fetch('/api/User/refresh-token', {
                      method: 'POST',
                      headers: { 'Content-Type': 'application/json' },
                      body: JSON.stringify({ refreshToken: storedUser.refreshToken }),
                    });
                    
                    if (refreshResponse.ok) {
                      const refreshResult = await refreshResponse.json();
                      const newAccessToken = refreshResult.AccessToken || refreshResult.accessToken;
                      const newRefreshToken = refreshResult.RefreshToken || refreshResult.refreshToken;
                      
                      if (newAccessToken && newRefreshToken) {
                        // Update localStorage with new tokens
                        const updatedUser = {
                          ...storedUser,
                          token: newAccessToken,
                          refreshToken: newRefreshToken,
                        };
                        localStorage.setItem('user', JSON.stringify(updatedUser));
                        tokenToUse = newAccessToken;
                      }
                    }
                  } catch (refreshError) {
                      Sentry.captureException(refreshError);
                    // If refresh fails, continue with old token (will likely get 401 but won't crash)
                    if (typeof import.meta !== 'undefined' && import.meta.env && import.meta.env.MODE === 'development') {
                      console.debug('GlobalCacheBanner: Token refresh failed, using existing token', refreshError);
                    }
                  }
                }
              } catch (tokenParseError) {
                  Sentry.captureException(tokenParseError);
                // If token parsing fails, just use the token as-is
                // This handles edge cases with malformed tokens without crashing the banner
                if (typeof import.meta !== 'undefined' && import.meta.env && import.meta.env.MODE === 'development') {
                  console.debug('GlobalCacheBanner: Could not parse token expiry, using token as-is', tokenParseError);
                }
              }
              
              // Now make the cache-progress request with the (possibly refreshed) token
              const controller = new AbortController();
              // Use a timeout to abort the request if it takes too long. The timeout must always
              // be cleared to avoid a lingering timer if fetch throws due to a network error.
              const abortTimeoutId = setTimeout(() => controller.abort(), CACHE_PROGRESS_TIMEOUT_MS);

              let res;
              try {
                res = await fetch('/api/system/cache-progress', {
                  headers: {
                    // Authorization header is required for JWT authentication.
                    // Content-Type is intentionally omitted here because this is a GET request
                    // without a request body, and setting Content-Type would be unnecessary.
                    'Authorization': `Bearer ${tokenToUse}`,
                  },
                  signal: controller.signal,
                });
              } finally {
                // Always clear the timeout, even if fetch throws or is aborted.
                clearTimeout(abortTimeoutId);
              }
              
              if (res.ok) {
                const json = await res.json();
                startTransition(() => setCacheStatus(json));
                lastWarmingRef.current = !!json?.isWarmingUp;
                lastCacheTsRef.current = Date.now();
                if (json?.isWarmingUp) {
                  startTransition(() => setVisibleType('cache'));
                  currentTypeRef.current = 'cache';
                } else {
                  decideAndRender(true, prevState);
                }
              } else {
                // On 401 or other auth errors, silently fail and just show appropriate banner
                decideAndRender(true, prevState);
              }
            } else {
              decideAndRender(true, prevState);
            }
          }
        } catch (err) {
            Sentry.captureException(err);
          // Handle JSON parse errors, network errors, or timeouts - silently fail for the user.
          // This prevents corrupted localStorage data or transient network issues from crashing the banner.
          // For development environments, log the error to aid in diagnosing issues without impacting production.
          if (typeof import.meta !== 'undefined' && import.meta.env && import.meta.env.MODE === 'development') {
            console.debug('GlobalCacheBanner: cache-progress polling failed; falling back to banner-only state.', err);
          }
          decideAndRender(true, prevState);
        }
      } else {
        // Stabilized DOWN or unauthenticated
        decideAndRender(false, prevState);
      }

      if (!stop) timer = setTimeout(check, POLL_MS);
    };

    // Add startup delay to allow session to fully establish after login
    // This prevents 401 errors from polling before the backend has committed
    // the session transaction or before JWT middleware recognizes the token
    startupTimer = setTimeout(() => {
      if (!stop) check();
    }, STARTUP_DELAY_MS);
    
    return () => { 
      stop = true; 
      if (timer) clearTimeout(timer); 
      if (startupTimer) clearTimeout(startupTimer);
    };
  }, [user]);

  if (!user) return null;

  /**
   * Use server-computed percent for accurate progress display.
   * The server ensures the percent is calculated safely even when totals
   * grow dynamically due to multi-threaded caching operations.
   * Falls back to client-side calculation if server percent is not available.
   */
  const percent = typeof cacheStatus?.percent === 'number'
    ? Math.min(100, Math.max(0, cacheStatus.percent))
    : (() => {
        // Fallback: compute locally using display-safe values from server
        const total = Number.isFinite(cacheStatus?.displayTotal) 
          ? cacheStatus.displayTotal 
          : (Number.isFinite(cacheStatus?.totalJobs) ? cacheStatus.totalJobs : 0);
        const completed = Number.isFinite(cacheStatus?.displayCompleted) 
          ? cacheStatus.displayCompleted 
          : (Number.isFinite(cacheStatus?.completedJobs) ? cacheStatus.completedJobs : 0);
        const safeTotal = Math.max(total, 1);
        const safeCompletedForPercent = Math.min(completed, safeTotal);
        const raw = cacheStatus?.isWarmingUp && total > 0 ? Math.round((safeCompletedForPercent / safeTotal) * 100) : 0;
        return Math.min(100, Math.max(0, raw));
      })();

  /**
   * Use server-provided displayCompleted and displayTotal for accurate progress counts.
   * These values are sanitized on the server to handle the case where completed
   * might momentarily exceed total during multi-threaded cache operations.
   * Fall back to raw values if display values are not available.
   */
  const displayCompleted = Number.isFinite(cacheStatus?.displayCompleted) 
    ? cacheStatus.displayCompleted 
    : (Number.isFinite(cacheStatus?.completedJobs) ? cacheStatus.completedJobs : 0);
  const displayTotal = Number.isFinite(cacheStatus?.displayTotal) 
    ? cacheStatus.displayTotal 
    : (Number.isFinite(cacheStatus?.totalJobs) ? cacheStatus.totalJobs : 0);

  const onClose = () => {
    const t = currentTypeRef.current;
    if (t === 'down' || t === 'up') {
      localStorage.setItem(KEY_SHOWN_TYPE, t);
    }
    setVisibleType(null);
    currentTypeRef.current = null;
  };

  if (visibleType === 'cache') {
    const elapsed = cacheStatus?.elapsedSeconds;
    const eta = cacheStatus?.estimatedRemainingSeconds;
    const currentJob = cacheStatus?.currentJobName;

    const formatTime = (secs) => {
      if (secs == null || secs < 0) return null;
      if (secs < 60) return `${Math.floor(secs)}s`;
      const m = Math.floor(secs / 60);
      const s = Math.floor(secs % 60);
      return `${m}m ${s}s`;
    };

    return (
      <div className="global-cache-banner">
        <div className="dashboard-card cache-banner">
          <div className="cache-row">
            <div className="cache-text">
              <GearIcon />
              <span>Building caches... {percent}% ({displayCompleted}/{displayTotal})</span>
              {elapsed != null && (
                <span className="cache-meta"> — {formatTime(elapsed)} elapsed</span>
              )}
              {eta != null && eta > 0 && (
                <span className="cache-meta"> — ~{formatTime(eta)} remaining</span>
              )}
            </div>
            <div className="cache-progress">
              <div className="cache-progress-bar" style={{ width: `${percent}%` }} />
            </div>
            <CloseButton onClick={onClose} aria="Dismiss cache banner" />
          </div>
          {currentJob && (
            <div className="cache-detail-row">
              <span className="cache-current-job">
                <GearIcon spin={true} /> {currentJob}
              </span>
            </div>
          )}
        </div>
      </div>
    );
  }

  if (visibleType === 'down') {
    return (
      <div className="global-cache-banner">
        <div className="dashboard-card cache-banner">
          <div className="cache-row center">
            <div className="cache-text" style={{ display:'flex', alignItems:'center', gap:8 }}>
              <RefreshIcon /> The server is currently unavailable - Retrying...
            </div>
            <CloseButton className="right" onClick={onClose} aria="Dismiss server down banner" />
          </div>
        </div>
      </div>
    );
  }

  if (visibleType === 'up') {
    return (
      <div className="global-cache-banner">
        <div className="dashboard-card cache-banner">
          <div className="cache-row">
            <div className="cache-text"><GearIcon spin={false} /> Server is back online</div>
            <CloseButton onClick={onClose} aria="Dismiss server up banner" />
          </div>
        </div>
      </div>
    );
  }

  return null;
};

export default GlobalCacheBanner;
